import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";
import { useBatchUpload, type UseBatchUpload } from "../hooks/useBatchUpload";
import { decodeToken } from "../utils/tokenUtils";

interface BatchUploadContextValue {
  batch: UseBatchUpload;
  companyId: string | null;
  shareWithCompany: boolean;
  setShareWithCompany: (value: boolean) => void;
  ensureReceiptCohortAccess: (cohortId: string) => Promise<string>;
}

const BatchUploadContext = createContext<BatchUploadContextValue | null>(null);

interface SessionProps {
  children: React.ReactNode;
  token: string | null;
  refreshAuth: ReturnType<typeof useAuth>["refreshAuth"];
}

/**
 * Owns the active folder upload for one authenticated user. Keeping this above
 * the route tree lets uploads and analysis polling continue while the user
 * works elsewhere in the app.
 */
const BatchUploadSession: React.FC<SessionProps> = ({ children, token, refreshAuth }) => {
  const claims = useMemo(
    () => (token ? (decodeToken(token) as { sub?: string; company_id?: string } | null) : null),
    [token],
  );
  const privateCohortId = claims?.sub ? `self-upload-${claims.sub}` : null;
  const companyId = claims?.company_id ?? null;
  const companyCohortId = companyId ? `company-upload-${companyId}` : null;
  const [shareWithCompany, setShareWithCompany] = useState(false);
  const selectedCohortId = shareWithCompany ? companyCohortId : privateCohortId;

  const ensureReceiptCohortAccess = useCallback(
    async (receiptCohortId: string) => {
      const receiptCompanyId = receiptCohortId.startsWith("company-upload-")
        ? receiptCohortId.slice("company-upload-".length)
        : null;
      const tokenCompanyId = token
        ? (decodeToken(token) as { company_id?: string } | null)?.company_id
        : null;
      if (!token) {
        throw new Error("Your session expired — sign in again before viewing this upload.");
      }
      if (!receiptCompanyId || receiptCompanyId === tokenCompanyId) return token;

      const refreshed = await refreshAuth();
      const refreshedCompanyId = refreshed?.token
        ? (decodeToken(refreshed.token) as { company_id?: string } | null)?.company_id
        : null;
      if (!refreshed || refreshedCompanyId !== receiptCompanyId) {
        throw new Error(
          "Your company membership changed. Please sign in again before viewing this upload.",
        );
      }
      return refreshed.token;
    },
    [refreshAuth, token],
  );

  const activeBatch = useBatchUpload(
    token,
    selectedCohortId,
    shareWithCompany,
    ensureReceiptCohortAccess,
  );

  // A new batch starts private by default and replaces the retained result from
  // the previous batch. Resetting from the upload page clears it explicitly.
  const begin = useCallback(
    (files: File[]) => {
      setShareWithCompany(false);
      activeBatch.begin(files);
    },
    [activeBatch.begin],
  );
  const reset = useCallback(() => {
    setShareWithCompany(false);
    activeBatch.reset();
  }, [activeBatch.reset]);

  const batch = useMemo<UseBatchUpload>(
    () => ({ ...activeBatch, begin, reset }),
    [activeBatch, begin, reset],
  );
  const value = useMemo<BatchUploadContextValue>(
    () => ({
      batch,
      companyId,
      shareWithCompany,
      setShareWithCompany,
      ensureReceiptCohortAccess,
    }),
    [batch, companyId, ensureReceiptCohortAccess, shareWithCompany],
  );

  return <BatchUploadContext.Provider value={value}>{children}</BatchUploadContext.Provider>;
};

export const BatchUploadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token, userId, refreshAuth } = useAuth();
  const sub = token ? (decodeToken(token) as { sub?: string } | null)?.sub : null;

  // Token refreshes preserve `sub`, so an active batch survives them. Logout or
  // an account change produces a different key and unmounts the old machine.
  const sessionKey = userId && sub ? `${userId}:${sub}` : "signed-out";
  return (
    <BatchUploadSession key={sessionKey} token={token} refreshAuth={refreshAuth}>
      {children}
    </BatchUploadSession>
  );
};

export function useBatchUploadContext(): BatchUploadContextValue {
  const context = useContext(BatchUploadContext);
  if (!context) {
    throw new Error("useBatchUploadContext must be used within BatchUploadProvider");
  }
  return context;
}
